import { Component, Input, OnInit } from '@angular/core';
import { deepCopy, DwComponent, DwComponentType, DwFormComponent, DwFormState, DwMetaDataFormApi, DwMetaDataFormStateService } from '@devwareapps/devware-cap';
import { ChangeLogEntry, ChangeLogEvent, DiffEntry, getObservableChangeLog } from '../../util/object-differ.class';
import { BehaviorSubject, combineLatest, merge, Observable, Subject } from 'rxjs';
import { map, scan } from 'rxjs/operators';


@DwComponent({
  key: 'dw-form-debug-log',
  name: 'Devware Form Debug Log',
  componentType: DwComponentType.formSection,
  isGlobal: true
})
@Component({
  selector: 'app-dw-form-debug-log',
  templateUrl: './dw-form-debug-log.component.html',
  styleUrls: ['./dw-form-debug-log.component.scss']
})
export class DwFormDebugLogComponent implements OnInit {
  formApi: DwMetaDataFormApi;

  entries$!: Observable<ChangeLogEntry[]>;
    @Input() changeLog$!: Observable<ChangeLogEntry>;

  constructor(private dwMetaDataFormStateService: DwMetaDataFormStateService,) {
    this.formApi = this.dwMetaDataFormStateService.state.formApi;
  }

  getStreams(): Observable<ChangeLogEntry> {
    const formChanges = getObservableChangeLog(
        this.formApi.getFormDataChanges(0)
        .pipe(map(data => deepCopy(data)))
        , null, 'formData')
      
    const variableChanges = getObservableChangeLog(this.formApi.getVariableContext(), null, 'variableContext');

    const formContextChanges = getObservableChangeLog(this.dwMetaDataFormStateService.stateChanged.pipe(map(state => this.MapState(state))), null, 'formState');

    return merge(formChanges, variableChanges, formContextChanges);
  }

  private MapState(state: DwFormState): any {
    return {
      ...state,
      rootFormGroup: null,
          formApi: null,
          formConfig: null,
          context: null,
          sectionStates: null,
          originalData: null
    };
  }
  
  /** user actions */
  private clearAll$ = new Subject<void>();
  private clearType$ = new Subject<string>();
  private clearEntry$ = new Subject<string>();
  private searchTerm$ = new BehaviorSubject<string>('');

  /** public stream consumed by template */
  filteredEntries$!: Observable<ChangeLogEntry[]>;

  ngOnInit(): void {
    this.setupStreams(this.getStreams());
  }

  /**
   * Sets up the RxJS pipeline for:
   * - merging changeLog$ + UI commands
   * - accumulating state
   * - applying search filter
   */
  private setupStreams(stream: Observable<ChangeLogEntry>): void {
    //
    // 1. Create unified event stream
    //
    const events$ = merge<ChangeLogEvent>(
      stream.pipe(
        map(entry => ({ kind: 'add', entry } as ChangeLogEvent))
      ),
      this.clearAll$.pipe(
        map(() => ({ kind: 'clearAll' } as ChangeLogEvent))
      ),
      this.clearType$.pipe(
        map(diffType => ({ kind: 'clearType', diffType } as ChangeLogEvent))
      ),
      this.clearEntry$.pipe(
        map(timestamp => ({ kind: 'clearEntry', timestamp } as ChangeLogEvent))
      )
    );

    //
    // 2. Accumulate list of ChangeLogEntry items
    //
    const entries$ = events$.pipe(
      scan<ChangeLogEvent, ChangeLogEntry[]>((state, event) => {
        switch (event.kind) {
          case 'add': {
            const updated = [event.entry, ...state];
            return updated.slice(0, 200);
          }
          case 'clearAll':
            return [];
          case 'clearType':
            return state.filter(e => e.diffType !== event.diffType);
          case 'clearEntry':
            return state.filter(e => e.timestamp !== event.timestamp);
          default:
            return state;
        }
      }, [])
    );

    //
    // 3. Apply search filtering
    //
    this.filteredEntries$ = combineLatest([entries$, this.searchTerm$]).pipe(
      map(([entries, term]) => {
        const query = term.trim().toLowerCase();
        if (!query) return entries;
        return entries.filter(entry => this.matchesSearch(entry, query));
      })
    );
  }

  /** search matcher (path, type, timestamp, previous/current values) */
  private matchesSearch(entry: ChangeLogEntry, query: string): boolean {
    if (entry.diffType.toLowerCase().includes(query)) return true;
    if (entry.timestamp.toLowerCase().includes(query)) return true;

    return entry.changeLog.some(diff => {
      if (diff.path.toLowerCase().includes(query)) return true;
      if (diff.type.toLowerCase().includes(query)) return true;
      const prev = JSON.stringify(diff.previous ?? '').toLowerCase();
      const curr = JSON.stringify(diff.current ?? '').toLowerCase();
      return prev.includes(query) || curr.includes(query);
    });
  }

  // UI actions
  onSearch(term: string) {
    this.searchTerm$.next(term);
  }

  onClearAll() {
    this.clearAll$.next();
  }

  onClearType(diffType: string) {
    this.clearType$.next(diffType);
  }

  onClearEntry(entry: ChangeLogEntry) {
    this.clearEntry$.next(entry.timestamp);
  }

  // trackBy functions
  trackByTimestamp(_i: number, entry: ChangeLogEntry) {
    return entry.timestamp;
  }

  trackByDiff(_i: number, diff: DiffEntry) {
    return diff.path + diff.type + diff.previous + diff.current;
  }

  getBadgeClass(type: string) {
    switch (type) {
      case 'added':   return 'badge badge--added';
      case 'removed': return 'badge badge--removed';
      case 'changed':
      default:        return 'badge badge--changed';
    }
  }
}